import {
  FAIL_REASON_LABELS,
  JOB_PURPOSE_LABELS,
  JOB_STATUS_LABELS,
  Job,
  type JobProblem,
  OkResponse,
  PrinterSlotList,
  addDaysIso,
  formatBRL,
  formatDate,
  isoDateInSaoPaulo,
  formatIsoDate,
  monthLabel,
} from '@deizi/shared';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Icon } from '../../components/Icon';
import { Combobox } from '../../components/Combobox';
import { printerOption, spoolOption } from '../../components/pickers';
import { Drawer } from '../../components/Overlays';
import { PageHead } from '../../components/PageHead';
import { EmptyState, ErrorBox, Skeleton } from '../../components/States';
import { useToast } from '../../components/Toast';
import { ApiClientError, api } from '../../lib/api';
import { usePrinters } from '../../lib/catalog-api';
import { useSpools } from '../../lib/inventory-api';
import { errorMessage } from '../../lib/messages';
import { formatHm } from '../../lib/numbers';
import {
  destLongText,
  destText,
  jobMeta,
  secondsLeft,
  useInvalidateQueue,
  useFailedJobs,
  useJob,
  useOwnPrints,
  useQueue,
  useTimeline,
  useToday,
  whenText,
} from '../../lib/queue-api';
import { readPref, writePref } from '../../lib/storage';
import { useIsPhone } from '../../lib/use-media';
import { Swatch } from '../inventory/spool-common';
import { FailDialog, FinishDialog, PastPrintDialog, PrintForStockDialog } from './dialogs';

type Dialog = { kind: 'finish' | 'fail'; job: Job } | { kind: 'stock' | 'past' } | null;

/** The current time, refreshed every 30 s so countdowns move. */
function useNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(t);
  }, []);
  return now;
}

function Thumb({ job, size = 52 }: { job: Job; size?: number }) {
  return job.thumbnailUrl ? (
    <img
      src={job.thumbnailUrl}
      alt=""
      style={{
        width: size,
        height: size,
        objectFit: 'contain',
        borderRadius: 10,
        background: 'var(--sunk)',
        flex: `0 0 ${size}px`,
      }}
    />
  ) : (
    <span className="ph" style={{ width: size, height: size, flex: `0 0 ${size}px`, fontSize: 11 }}>
      placa
    </span>
  );
}

function useJobActions() {
  const toast = useToast();
  const invalidate = useInvalidateQueue();
  const onError = (err: unknown) => {
    if (err instanceof ApiClientError && err.status === 409) toast(err.message);
    else toast(errorMessage(err));
  };
  const start = useMutation({
    mutationFn: (j: Job) => api.post(`/jobs/${j.id}/start`, Job, {}),
    onSuccess: (j) => {
      void invalidate();
      toast(`${j.number} começou. Termina ${j.expectedEndAt ? whenText(j.expectedEndAt) : ''}.`);
    },
    onError,
  });
  const move = useMutation({
    mutationFn: (v: { job: Job; body: object }) =>
      api.post(`/jobs/${v.job.id}/move`, OkResponse, v.body),
    onSuccess: invalidate,
    onError,
  });
  const fix = useMutation({
    mutationFn: (v: { job: Job; lineId: string; spoolId: string }) =>
      api.patch(`/jobs/${v.job.id}`, Job, { spools: [{ lineId: v.lineId, spoolId: v.spoolId }] }),
    onSuccess: (j, v) => {
      void invalidate();
      const line = j.filaments.find((f) => f.id === v.lineId);
      toast(
        `${j.number}: ${line?.filament.name ?? 'filamento'} agora em ${line?.spool?.code ?? ''}.`,
      );
    },
    onError,
  });
  const swap = useMutation({
    mutationFn: (s: NonNullable<JobProblem['swap']>) =>
      api.post(`/printers/${s.printerId}/slots/${s.toSlot}/load`, PrinterSlotList, {
        spoolId: s.spoolId,
      }),
    onSuccess: (_r, s) => {
      void invalidate();
      toast(
        `${s.spoolCode} foi para o slot ${s.toSlot}${s.otherSpoolCode ? `; ${s.otherSpoolCode} foi para o slot ${s.fromSlot}` : ''}. Troque também na impressora.`,
      );
    },
    onError,
  });
  return { start, move, fix, swap };
}

function Problems({ job, compact = false }: { job: Job; compact?: boolean }) {
  const { fix, swap } = useJobActions();
  const phone = useIsPhone();
  if (!job.problems.length) return null;
  return (
    <div className="col" style={{ gap: 6 }}>
      {job.problems.map((p: JobProblem, i) => (
        <div
          key={`${p.code}-${i}`}
          className={p.blocking ? (p.code === 'short' ? 'alert warn' : 'alert err') : 'alert'}
          style={{ padding: compact ? 8 : 10 }}
        >
          <Icon name="alert" className="ic" />
          <div className="grow col" style={{ gap: 6 }}>
            <span className="xs">{p.message}</span>
            {p.fix && p.lineId ? (
              <button
                type="button"
                className="btn sm"
                style={{ alignSelf: 'flex-start' }}
                onClick={() => fix.mutate({ job, lineId: p.lineId!, spoolId: p.fix!.spoolId })}
              >
                Usar {p.fix.spoolCode}
                {p.fix.sealed ? ' (lacrado)' : ''}
              </button>
            ) : null}
            {p.swap ? (
              <button
                type="button"
                className="btn sm"
                style={{ alignSelf: 'flex-start' }}
                disabled={swap.isPending}
                onClick={() => swap.mutate(p.swap!)}
              >
                {phone ? 'Trocar slots' : 'Trocar carretéis de slot'}
              </button>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}

/** "Trocar carretéis de slot" for each wrong-nozzle line (Fila-Desktop aside). */
function SwapButtons({ job }: { job: Job }) {
  const { swap } = useJobActions();
  return job.problems.flatMap((p, i) =>
    p.swap ? (
      <button
        key={`${p.lineId}-${i}`}
        type="button"
        className="btn sm"
        style={{ alignSelf: 'flex-start' }}
        disabled={swap.isPending}
        onClick={() => swap.mutate(p.swap!)}
      >
        Trocar carretéis de slot
      </button>
    ) : (
      []
    ),
  );
}

function badgeFor(j: Job): { cls: string; text: string } {
  const blocking = j.problems.find((p) => p.blocking);
  if (j.redoOf) return { cls: 'badge err', text: 'Refazer' };
  if (blocking?.code === 'short' || blocking?.code === 'no_spool')
    return { cls: 'badge warn', text: 'Filamento' };
  if (blocking?.code === 'wrong_nozzle') return { cls: 'badge err', text: 'Bico errado' };
  if (blocking?.code === 'material') return { cls: 'badge err', text: 'Material' };
  if (j.dueOn) return { cls: 'badge warn', text: `até ${formatIsoDate(j.dueOn).slice(0, 5)}` };
  return { cls: 'badge', text: 'na fila' };
}

function PrintingCard({
  job,
  now,
  onFinish,
  onFail,
}: {
  job: Job;
  now: number;
  onFinish: () => void;
  onFail: () => void;
}) {
  const left = secondsLeft(job, now);
  const pct =
    job.expectedEndAt && job.startedAt
      ? Math.min(
          100,
          Math.round(((now - new Date(job.startedAt).getTime()) / 1000 / job.estSeconds) * 100),
        )
      : 0;
  return (
    <div className="card col" style={{ gap: 10, borderColor: 'var(--pe)' }}>
      <div className="row" style={{ gap: 12 }}>
        <Thumb job={job} />
        <div className="grow col" style={{ gap: 2, minWidth: 0 }}>
          <span className="row between" style={{ gap: 8 }}>
            <b className="trunc">{job.label}</b>
            <span className="fr tab" style={{ fontSize: 20 }}>
              {formatHm(left)}
            </span>
          </span>
          <span className="xs muted">
            {job.number} · {destText(job)}
            {job.slicedFile ? ` · ${job.slicedFile.name} v${job.slicedFile.version}` : ''}
            {job.expectedEndAt ? ` · ${whenText(job.expectedEndAt, 'termina ')}` : ''}
          </span>
        </div>
      </div>
      <div className="layers thin">
        <div className="fill" style={{ width: `${pct}%` }} />
        <div className="nozzle" style={{ left: `${pct}%` }} />
      </div>
      <div className="row" style={{ gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" className="btn sm d" onClick={onFail}>
          Falhou
        </button>
        <button type="button" className="btn sm p" onClick={onFinish}>
          Concluir
        </button>
      </div>
    </div>
  );
}

/** "Verificação de compatibilidade" (Fila-Desktop): material, nozzle and filament of a run. */
function CompatRows({ job }: { job: Job }) {
  const has = (code: string, lineId?: string) =>
    job.problems.some((p) => p.code === code && (lineId === undefined || p.lineId === lineId));
  const materials = [...new Set(job.filaments.map((f) => f.filament.material))].join(' + ');
  const rows: { label: string; value: string; ok: boolean }[] = [
    {
      label: materials || 'Material',
      value: has('material') ? 'não configurado' : 'suportado',
      ok: !has('material'),
    },
    ...job.filaments
      .filter((f) => f.nozzle)
      .map((f) => {
        const wrong = has('wrong_nozzle', f.id);
        const slot = f.spool?.location?.slot;
        return {
          label: `${f.filament.material} no bico ${f.nozzle === 'main' ? 'principal' : 'auxiliar'}`,
          value: wrong
            ? `slot ${slot ?? '?'} → ${f.nozzle === 'main' ? 'auxiliar' : 'principal'}`
            : slot
              ? `slot ${slot}`
              : 'ok',
          ok: !wrong,
        };
      }),
    {
      label: 'Filamento suficiente',
      value: has('short') || has('no_spool') ? 'falta' : 'ok',
      ok: !has('short') && !has('no_spool'),
    },
  ];
  const blocking = job.problems.some((p) => p.blocking);
  return (
    <div className="col" style={{ gap: 2 }}>
      {rows.map((r) => (
        <div key={r.label} className="kv sm">
          <span className="muted">{r.label}</span>
          <span className={r.ok ? 'v' : 'v err'} style={r.ok ? undefined : { color: 'var(--err)' }}>
            {r.value}
          </span>
        </div>
      ))}
      {blocking ? null : (
        <p className="sm" style={{ marginTop: 6 }}>
          Tudo certo para imprimir.
        </p>
      )}
    </div>
  );
}

function JobDrawer({
  job,
  startable,
  onClose,
  onFinish,
  onFail,
}: {
  job: Job;
  /** First in a free printer's lane (Fila-Desktop: "Iniciar" only there). */
  startable: boolean;
  onClose: () => void;
  onFinish: () => void;
  onFail: () => void;
}) {
  const toast = useToast();
  const invalidate = useInvalidateQueue();
  const { start, move } = useJobActions();
  const printers = usePrinters();
  const spools = useSpools({ active: true });
  const setSpool = useMutation({
    mutationFn: (v: { lineId: string; spoolId: string | null }) =>
      api.patch(`/jobs/${job.id}`, Job, { spools: [v] }),
    onSuccess: (j, v) => {
      void invalidate();
      toast(
        `${j.number}: agora em ${j.filaments.find((f) => f.id === v.lineId)?.spool?.code ?? 'nenhum carretel'}.`,
      );
    },
    onError: (err) => toast(errorMessage(err)),
  });
  const queued = job.status === 'queued';
  return (
    <Drawer
      kicker={`${job.number} · ${JOB_STATUS_LABELS[job.status]}`}
      title={job.label}
      onClose={onClose}
    >
      <div>
        <div className="kv sm">
          <span className="muted">{job.purpose ? 'Finalidade' : 'Pedido'}</span>
          <span className="v">
            {job.purpose
              ? `${JOB_PURPOSE_LABELS[job.purpose]} · fora de pedido`
              : job.orders.length
                ? job.orders.map((o, i) => (
                    <span key={o.id}>
                      {i ? ', ' : ''}
                      <Link to={`/pedidos/${o.id}`}>{o.number}</Link>
                    </span>
                  ))
                : 'Pronta entrega'}
          </span>
        </div>
        <div className="kv sm">
          <span className="muted">Destino</span>
          <span className="v">{destLongText(job)}</span>
        </div>
        <div className="kv sm">
          <span className="muted">Arquivo e versão</span>
          <span className="v">
            {job.slicedFile ? `${job.slicedFile.name} · v${job.slicedFile.version}` : '—'}
          </span>
        </div>
        <div className="kv sm">
          <span className="muted">Placa</span>
          <span className="v">
            {job.plateCount > 1 ? `${job.plateIndex + 1} de ${job.plateCount}` : 'única'}
          </span>
        </div>
        <div className="kv sm">
          <span className="muted">Tempo estimado</span>
          <span className="v">{formatHm(job.estSeconds)}</span>
        </div>
        <div className="kv sm">
          <span className="muted">Impressora</span>
          {queued ? (
            <Combobox
              size="sm"
              ariaLabel="Impressora"
              style={{ width: 'auto', minWidth: 170 }}
              value={job.printer?.id ?? ''}
              options={[
                { value: '', label: 'Sem impressora', pinned: 'top' },
                ...(printers.data?.data ?? []).map(printerOption),
              ]}
              onChange={(v) => move.mutate({ job, body: { printerId: v || null } })}
            />
          ) : (
            <span className="v">{job.printer?.name ?? '—'}</span>
          )}
        </div>
      </div>
      <span className="lbl">Carretéis</span>
      {job.filaments.map((f) => {
        const options = (spools.data?.data ?? []).filter((s) => s.filament.id === f.filament.id);
        const enough = f.spool ? f.spool.remainingGrams >= f.needGrams : false;
        return (
          <div
            key={f.id}
            className="card sunk col"
            style={{ gap: 6, padding: 12, borderRadius: 12 }}
          >
            <div className="row between" style={{ gap: 8 }}>
              <span className="row" style={{ gap: 8 }}>
                <Swatch hex={f.filament.colorHex} />
                <b className="sm">{f.filament.name}</b>
              </span>
              <span className="xs muted">precisa {Math.round(f.needGrams)} g</span>
            </div>
            <Combobox
              ariaLabel={`Carretel de ${f.filament.name}`}
              disabled={!queued}
              value={f.spool?.id ?? ''}
              options={[
                { value: '', label: 'Sem carretel', pinned: 'top' },
                ...options.map(spoolOption),
              ]}
              selected={f.spool ? spoolOption(f.spool) : null}
              onChange={(v) => setSpool.mutate({ lineId: f.id, spoolId: v || null })}
            />
            <span className="row" style={{ gap: 8 }}>
              {f.nozzle ? (
                <span className="xs muted">
                  {f.nozzle === 'main' ? 'bico principal' : 'bico auxiliar'}
                </span>
              ) : null}
              {f.spool ? (
                <span className={enough ? 'badge ok' : 'badge warn'}>
                  {enough
                    ? 'suficiente'
                    : `faltam ${Math.ceil(f.needGrams - f.spool.remainingGrams)} g`}
                </span>
              ) : null}
            </span>
          </div>
        );
      })}
      {queued ? (
        <>
          <span className="lbl">Compatibilidade</span>
          <CompatRows job={job} />
        </>
      ) : null}
      <Problems job={job} />
      {job.failReason ? (
        <p className="sm">
          Falhou {job.failPct}% · {FAIL_REASON_LABELS[job.failReason]} ·{' '}
          {Math.round(job.wastedGrams ?? 0)} g perdidos
        </p>
      ) : null}
      <div className="row wrap" style={{ gap: 8 }}>
        {queued ? (
          <>
            <button
              type="button"
              className="btn sm"
              onClick={() => move.mutate({ job, body: { direction: 'up' } })}
            >
              ↑ Subir
            </button>
            <button
              type="button"
              className="btn sm"
              onClick={() => move.mutate({ job, body: { direction: 'down' } })}
            >
              ↓ Descer
            </button>
            {startable ? (
              <button type="button" className="btn sm p" onClick={() => start.mutate(job)}>
                Iniciar
              </button>
            ) : null}
            {job.printer ? (
              <>
                <button type="button" className="btn sm" onClick={onFinish}>
                  Já foi impressa
                </button>
                <button type="button" className="btn sm g" onClick={onFail}>
                  Já falhou
                </button>
              </>
            ) : null}
          </>
        ) : null}
        {job.status === 'printing' ? (
          <>
            <button type="button" className="btn sm d" onClick={onFail}>
              Falhou
            </button>
            <button type="button" className="btn sm p" onClick={onFinish}>
              Concluir
            </button>
          </>
        ) : null}
      </div>
    </Drawer>
  );
}

type TimelineWindow = '24h' | '3d' | 'all';
const WINDOW_HOURS: Record<Exclude<TimelineWindow, 'all'>, number> = { '24h': 24, '3d': 72 };
const HOUR = 3600_000;

/** Ruler marks for a span: every 3 h up to a day and a half, then 12 h, then days. */
function rulerTicks(from: number, to: number): { at: number; label: string; day: boolean }[] {
  const hours = (to - from) / HOUR;
  const step = hours <= 36 ? 3 : hours <= 96 ? 12 : hours <= 24 * 14 ? 24 : 48;
  const d = new Date(from);
  d.setMinutes(0, 0, 0);
  d.setHours(Math.ceil(d.getHours() / step) * step);
  const out: { at: number; label: string; day: boolean }[] = [];
  for (let t = d.getTime(); t < to && out.length < 40;) {
    const x = new Date(t);
    const midnight = x.getHours() === 0;
    out.push({
      at: t,
      day: midnight,
      label: midnight
        ? `${x.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', '')} ${String(x.getDate()).padStart(2, '0')}/${String(x.getMonth() + 1).padStart(2, '0')}`
        : `${x.getHours()}h`,
    });
    x.setHours(x.getHours() + step);
    t = x.getTime();
  }
  return out;
}

function Timeline() {
  const t = useTimeline();
  const [win, setWin] = useState<TimelineWindow>(() => {
    const saved = readPref('timelineWindow');
    return saved === '3d' || saved === 'all' ? saved : '24h';
  });
  if (!t.data) return null;
  const from = new Date(t.data.now).getTime();
  const lastFree = Math.max(...t.data.printers.map((p) => new Date(p.freeAt).getTime()), from);
  // "Tudo" spans the whole queue (at least a day); the others a fixed window from now.
  const to = win === 'all' ? Math.max(lastFree, from + 24 * HOUR) : from + WINDOW_HOURS[win] * HOUR;
  const span = to - from;
  const pct = (ms: number) => ((Math.min(Math.max(ms, from), to) - from) / span) * 100;
  const ticks = rulerTicks(from, to);
  const choose = (w: TimelineWindow) => {
    setWin(w);
    writePref('timelineWindow', w);
  };

  return (
    <section className="card col" style={{ gap: 10 }}>
      <div className="row between wrap" style={{ gap: 8 }}>
        <h2 style={{ fontSize: 18 }}>Linha do tempo</h2>
        <div className="seg" role="radiogroup" aria-label="Período da linha do tempo">
          {(
            [
              ['24h', '24 h'],
              ['3d', '3 dias'],
              ['all', 'Tudo'],
            ] as const
          ).map(([k, label]) => (
            <button
              key={k}
              type="button"
              role="radio"
              aria-checked={win === k}
              className={win === k ? 'on' : ''}
              onClick={() => choose(k)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {/* Ruler: hours, and the day at each midnight. */}
      <div className="xs muted" style={{ position: 'relative', height: 16 }} aria-hidden="true">
        <span style={{ position: 'absolute', left: 0 }}>agora</span>
        {ticks
          .filter((k) => pct(k.at) > 6 && pct(k.at) < 96)
          .map((k) => (
            <span
              key={k.at}
              className="tab"
              style={{
                position: 'absolute',
                left: `${pct(k.at)}%`,
                transform: 'translateX(-50%)',
                whiteSpace: 'nowrap',
                fontWeight: k.day ? 600 : undefined,
              }}
            >
              {k.label}
            </span>
          ))}
      </div>
      {t.data.printers.map((lane) => {
        const shown = lane.segments.filter(
          (s) => new Date(s.end).getTime() > from && new Date(s.start).getTime() < to,
        );
        const later = lane.segments.length - shown.length;
        return (
          <div key={lane.printer.id} className="col" style={{ gap: 6 }}>
            <div
              style={{
                position: 'relative',
                height: 30,
                borderRadius: 8,
                overflow: 'hidden',
                background: 'var(--sunk)',
              }}
            >
              {ticks.map((k) => (
                <span
                  key={k.at}
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    left: `${pct(k.at)}%`,
                    top: 0,
                    bottom: 0,
                    borderLeft: `1px ${k.day ? 'solid' : 'dashed'} var(--line)`,
                  }}
                />
              ))}
              {shown.map((s) => {
                const left = pct(new Date(s.start).getTime());
                const width = pct(new Date(s.end).getTime()) - left;
                const printing = s.status === 'printing';
                return (
                  <span
                    key={s.jobId}
                    title={`${s.number} · ${s.label} · ${whenText(s.start)} a ${whenText(s.end)}`}
                    className="xs trunc"
                    style={{
                      position: 'absolute',
                      top: 2,
                      bottom: 2,
                      left: `${left}%`,
                      width: `${width}%`,
                      padding: width >= 6 ? '0 6px' : 0,
                      lineHeight: '26px',
                      borderRadius: 6,
                      color: printing ? 'var(--pe-ink)' : 'var(--ink)',
                      background: printing ? 'var(--pe)' : 'var(--card)',
                      border: s.problem ? '1px dashed var(--warn)' : '1px solid var(--line)',
                    }}
                  >
                    {/* The name only where it fits; the tooltip always has it. */}
                    {width >= 6 ? s.label : null}
                  </span>
                );
              })}
            </div>
            <p className="sm muted">
              <b>{lane.printer.name}</b>{' '}
              {lane.segments.length
                ? `livre ${whenText(lane.freeAt)} · ${(lane.backlogSeconds / 3600).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} h na fila`
                : 'livre agora'}
              {later > 0
                ? ` · +${later} ${later === 1 ? 'impressão depois' : 'impressões depois'}`
                : ''}
            </p>
          </div>
        );
      })}
    </section>
  );
}

function TodayCard({ onOpen }: { onOpen: (j: Job) => void }) {
  const today = useToday();
  const rows = today.data?.data ?? [];
  if (!rows.length) return null;
  return (
    <section className="card col" style={{ gap: 4 }}>
      <h2 style={{ fontSize: 18 }}>Hoje</h2>
      {rows.map((j) => (
        <button
          key={j.id}
          type="button"
          className="listrow"
          style={{ width: '100%' }}
          onClick={() => onOpen(j)}
        >
          <span className="grow sm" style={{ textAlign: 'left' }}>
            {j.number} · {j.label} · {Math.round(j.actualGrams ?? 0)} g
          </span>
          <span className={j.status === 'done' ? 'badge ok' : 'badge err'}>
            {j.status === 'done' ? 'Concluída' : `Falhou ${j.failPct ?? 0}%`}
          </span>
        </button>
      ))}
    </section>
  );
}

/** "Ver impressões com falha" (Relatórios › Produção): the failed runs of a month. */
function FailedList({ month, onOpen }: { month: string; onOpen: (j: Job) => void }) {
  const failed = useFailedJobs(month);
  const rows = failed.data?.data ?? [];
  const cols = '90px 2fr 1fr 1.4fr 80px 90px';
  if (failed.isError)
    return <ErrorBox title="Não foi possível carregar" onRetry={() => void failed.refetch()} />;
  return (
    <section className="card flat">
      <p className="sm muted" style={{ padding: '14px 18px 4px' }}>
        {monthLabel(month)} · {rows.length} {rows.length === 1 ? 'falha' : 'falhas'} ·{' '}
        {Math.round(rows.reduce((n, j) => n + (j.wastedGrams ?? 0), 0))} g perdidos
      </p>
      <div className="gt" style={{ overflowX: 'auto' }}>
        <div className="gtr head" style={{ gridTemplateColumns: cols, minWidth: 680 }}>
          <span>Impressão</span>
          <span>Peça</span>
          <span>Impressora</span>
          <span>Falha</span>
          <span className="num">Perdido</span>
          <span className="num">Data</span>
        </div>
        {!failed.data ? <Skeleton rows={3} height={40} /> : null}
        {rows.map((j) => (
          <button
            key={j.id}
            type="button"
            className="gtr"
            style={{ gridTemplateColumns: cols, minWidth: 680 }}
            onClick={() => onOpen(j)}
          >
            <span className="tab">{j.number}</span>
            <span className="trunc">{j.label}</span>
            <span className="sm">{j.printer?.name ?? '—'}</span>
            <span className="sm">
              Falhou {j.failPct ?? 0}% · {j.failReason ? FAIL_REASON_LABELS[j.failReason] : '—'}
            </span>
            <span className="num">{Math.round(j.wastedGrams ?? 0)} g</span>
            <span className="num sm">
              {j.finishedAt ? formatDate(j.finishedAt).slice(0, 5) : '—'}
            </span>
          </button>
        ))}
        {failed.data && !rows.length ? (
          <div className="empty" style={{ padding: 24 }}>
            <p className="sm">Nenhuma impressão falhou neste mês.</p>
          </div>
        ) : null}
      </div>
    </section>
  );
}

/** "Impressões fora de pedido" of a month (link from Relatórios › Resultado do mês). */
function OwnPrintsList({ month, onOpen }: { month: string; onOpen: (j: Job) => void }) {
  const q = useOwnPrints(month);
  const rows = q.data?.data ?? [];
  const cols = '90px 2fr 1fr 1fr 80px 90px 90px';
  if (q.isError)
    return <ErrorBox title="Não foi possível carregar" onRetry={() => void q.refetch()} />;
  return (
    <section className="card flat">
      <p className="sm muted" style={{ padding: '14px 18px 4px' }}>
        {monthLabel(month)} · {rows.length} {rows.length === 1 ? 'impressão' : 'impressões'} ·{' '}
        {formatBRL(rows.reduce((n, j) => n + (j.realCostCentavos ?? 0), 0))}
      </p>
      <div className="gt" style={{ overflowX: 'auto' }}>
        <div className="gtr head" style={{ gridTemplateColumns: cols, minWidth: 720 }}>
          <span>Impressão</span>
          <span>O quê</span>
          <span>Finalidade</span>
          <span>Resultado</span>
          <span className="num">Filamento</span>
          <span className="num">Custo</span>
          <span className="num">Data</span>
        </div>
        {!q.data ? <Skeleton rows={3} height={40} /> : null}
        {rows.map((j) => (
          <button
            key={j.id}
            type="button"
            className="gtr"
            style={{ gridTemplateColumns: cols, minWidth: 720 }}
            onClick={() => onOpen(j)}
          >
            <span className="tab">{j.number}</span>
            <span className="trunc">{j.label}</span>
            <span className="sm">{j.purpose ? JOB_PURPOSE_LABELS[j.purpose] : '—'}</span>
            <span>
              {j.status === 'done' ? (
                <span className="badge ok">Concluída</span>
              ) : (
                <span className="badge err">Falhou</span>
              )}
            </span>
            <span className="num">{Math.round(j.actualGrams ?? 0)} g</span>
            <span className="num">{formatBRL(j.realCostCentavos ?? 0)}</span>
            <span className="num sm">
              {j.finishedAt ? formatDate(j.finishedAt).slice(0, 5) : '—'}
            </span>
          </button>
        ))}
        {q.data && !rows.length ? (
          <div className="empty" style={{ padding: 24 }}>
            <p className="sm">Nenhuma impressão fora de pedido neste mês.</p>
          </div>
        ) : null}
      </div>
    </section>
  );
}

/** Fila de impressão (boards Fila-Desktop, Fila-Celular, Fila-Dialogos). */
export function QueuePage() {
  const now = useNow();
  const phone = useIsPhone();
  const queue = useQueue();
  const printers = usePrinters();
  const [view, setView] = useState<'board' | 'list'>('board');
  const [dialog, setDialog] = useState<Dialog>(null);
  // "Novo › Impressão" in the top bar links here with ?nova=1.
  const [params, setParams] = useSearchParams();
  const newFromMenu = params.has('nova');
  // OrderPage links to "/fila?job=<id>" to open a run.
  const [openId, setOpenId] = useState<string | null>(() => params.get('job'));
  const failedMonth = params.get('falhas');
  const ownMonth = params.get('avulsas');
  const [phonePrinter, setPhonePrinter] = useState<string>('all');
  const [dragId, setDragId] = useState<string | null>(null);
  const { start, move } = useJobActions();
  const toast = useToast();
  const jobs = queue.data?.data ?? [];
  const today = useToday();
  const all = [...jobs, ...(today.data?.data ?? [])];
  const listed = all.find((j) => j.id === openId);
  const single = useJob(openId && !listed && queue.data && today.data ? openId : null);
  const open = listed ?? single.data ?? null;
  const lanes = [
    ...(printers.data?.data ?? []).map((p) => ({ id: p.id as string | null, name: p.name })),
    ...(jobs.some((j) => !j.printer) ? [{ id: null, name: 'Sem impressora' }] : []),
  ];
  const nextOf = (printerId: string | null) =>
    jobs.find((j) => j.status === 'queued' && j.printer?.id === printerId) ?? null;
  /** Fila-Desktop: "Iniciar" only on the first run of a free printer. */
  const startable = (j: Job) =>
    j.status === 'queued' &&
    !!j.printer &&
    nextOf(j.printer.id)?.id === j.id &&
    !jobs.some((x) => x.status === 'printing' && x.printer?.id === j.printer!.id);
  const openDialog = (kind: 'finish' | 'fail', job: Job) => setDialog({ kind, job });

  const header = (
    <PageHead
      crumb="Produzir"
      title="Fila de impressão"
      actions={
        <div className="row" style={{ gap: 10 }}>
          {phone ? null : (
            <div className="seg" role="radiogroup" aria-label="Visualização">
              <button
                type="button"
                role="radio"
                aria-checked={view === 'board'}
                className={view === 'board' ? 'on' : ''}
                onClick={() => setView('board')}
              >
                Quadro
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={view === 'list'}
                className={view === 'list' ? 'on' : ''}
                onClick={() => setView('list')}
              >
                Lista
              </button>
            </div>
          )}
          <button type="button" className="btn" onClick={() => setDialog({ kind: 'past' })}>
            Lançar impressão passada
          </button>
          <button type="button" className="btn p" onClick={() => setDialog({ kind: 'stock' })}>
            + Imprimir para estoque
          </button>
        </div>
      }
    />
  );

  const dialogs = (
    <>
      {dialog?.kind === 'finish' ? (
        <FinishDialog
          job={dialog.job}
          next={nextOf(dialog.job.printer?.id ?? null)}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === 'fail' ? (
        <FailDialog
          job={dialog.job}
          next={nextOf(dialog.job.printer?.id ?? null)}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === 'past' ? <PastPrintDialog onClose={() => setDialog(null)} /> : null}
      {dialog?.kind === 'stock' || newFromMenu ? (
        <PrintForStockDialog
          onClose={() => {
            setDialog(null);
            if (newFromMenu) setParams({}, { replace: true });
          }}
        />
      ) : null}
      {open ? (
        <JobDrawer
          job={open}
          startable={startable(open)}
          onClose={() => setOpenId(null)}
          onFinish={() => {
            setOpenId(null);
            openDialog('finish', open);
          }}
          onFail={() => {
            setOpenId(null);
            openDialog('fail', open);
          }}
        />
      ) : null}
    </>
  );

  if (queue.isPending)
    return (
      <>
        {header}
        <Skeleton rows={3} height={90} />
      </>
    );
  if (queue.isError) {
    return (
      <>
        {header}
        <ErrorBox
          title="Não foi possível carregar a fila"
          detail="Sem conexão. A impressora continua imprimindo."
          onRetry={() => void queue.refetch()}
        />
      </>
    );
  }
  if (ownMonth) {
    return (
      <>
        <PageHead
          crumb="Produzir"
          title="Impressões fora de pedido"
          actions={
            <div className="row" style={{ gap: 10 }}>
              <button type="button" className="btn" onClick={() => setDialog({ kind: 'past' })}>
                Lançar impressão passada
              </button>
              <Link className="btn" to="/fila">
                Voltar para a fila
              </Link>
            </div>
          }
        />
        <OwnPrintsList month={ownMonth} onOpen={(j) => setOpenId(j.id)} />
        {dialogs}
      </>
    );
  }
  if (failedMonth) {
    return (
      <>
        <PageHead
          crumb="Produzir"
          title="Impressões com falha"
          actions={
            <Link className="btn" to="/fila">
              Voltar para a fila
            </Link>
          }
        />
        <FailedList month={failedMonth} onOpen={(j) => setOpenId(j.id)} />
        {dialogs}
      </>
    );
  }
  const empty = jobs.length === 0;

  if (phone) {
    const mine = (j: Job) => phonePrinter === 'all' || j.printer?.id === phonePrinter;
    const printing = jobs.filter((j) => j.status === 'printing' && mine(j));
    const queued = jobs.filter((j) => j.status === 'queued' && mine(j));
    const printerList = printers.data?.data ?? [];
    return (
      <div className="col" style={{ gap: 14 }}>
        {header}
        {printerList.length ? (
          <div className="row sm" style={{ gap: 8 }}>
            <span className="muted">Impressora</span>
            <Combobox
              size="sm"
              ariaLabel="Impressora"
              style={{ width: 'auto', minWidth: 170 }}
              value={phonePrinter}
              options={[
                { value: 'all', label: 'Todas', pinned: 'top' },
                ...printerList.map(printerOption),
              ]}
              onChange={setPhonePrinter}
            />
          </div>
        ) : null}
        {printing.map((j) => (
          <PrintingCard
            now={now}
            key={j.id}
            job={j}
            onFinish={() => openDialog('finish', j)}
            onFail={() => openDialog('fail', j)}
          />
        ))}
        {!printing.length && !empty ? (
          <div className="card sm">
            {printerList
              .filter((p) => phonePrinter === 'all' || p.id === phonePrinter)
              .map((p) => p.name)
              .join(', ') || 'A impressora'}{' '}
            livre. Comece a próxima pela impressora ou pelo computador.
          </div>
        ) : null}
        {empty ? (
          <div className="card">
            <EmptyState
              title="Fila vazia"
              action={
                <button
                  type="button"
                  className="btn p"
                  onClick={() => setDialog({ kind: 'stock' })}
                >
                  Imprimir para estoque
                </button>
              }
            >
              A impressora está livre. Que tal adiantar peças para a pronta entrega?
            </EmptyState>
          </div>
        ) : null}
        {queued.length ? <h2 style={{ fontSize: 18 }}>A seguir · {queued.length}</h2> : null}
        {queued.map((j) => {
          const order = j.orders[0];
          return (
            <div key={j.id} className="card col" style={{ gap: 8 }}>
              <button
                type="button"
                className="row"
                style={{
                  gap: 10,
                  textAlign: 'left',
                  background: 'none',
                  border: 0,
                  padding: 0,
                  font: 'inherit',
                  color: 'inherit',
                  cursor: 'pointer',
                }}
                onClick={() => setOpenId(j.id)}
              >
                <Thumb job={j} size={44} />
                <span className="grow col" style={{ gap: 2, minWidth: 0 }}>
                  <span className="row between" style={{ gap: 6 }}>
                    <b className="trunc">
                      {j.position} · {j.label}
                    </b>
                    <span className={badgeFor(j).cls}>{badgeFor(j).text}</span>
                  </span>
                  <span className="xs muted">
                    {j.redoOf && order
                      ? `${order.number} · falhou ${j.redoOf.failPct ?? 0}%`
                      : jobMeta(j)}
                  </span>
                </span>
              </button>
              {order ? (
                <Link
                  to={`/pedidos/${order.id}`}
                  className={
                    order.shipBy && order.shipBy <= addDaysIso(isoDateInSaoPaulo(), 1)
                      ? 'xs badge warn'
                      : 'xs badge'
                  }
                  style={{ alignSelf: 'flex-start' }}
                >
                  {j.redoOf
                    ? `Abrir pedido ${order.number}`
                    : `${order.number}${order.shipBy ? ` · prazo de envio ${formatIsoDate(order.shipBy).slice(0, 5)}` : ''}`}
                </Link>
              ) : null}
              <Problems job={j} compact />
            </div>
          );
        })}
        <TodayCard onOpen={(j) => setOpenId(j.id)} />
        {dialogs}
      </div>
    );
  }

  // The run that needs a nozzle check first, else the next to print.
  const compatJob =
    jobs.find((j) => j.status === 'queued' && j.problems.some((p) => p.code === 'wrong_nozzle')) ??
    jobs.find((j) => j.status === 'queued') ??
    null;
  const listCols = '90px 2fr 1.3fr 80px 90px 1.2fr';
  return (
    <>
      {header}
      <Timeline />
      {empty ? (
        <section className="card" style={{ marginTop: 18 }}>
          <EmptyState
            title="Fila vazia"
            action={
              <button type="button" className="btn p" onClick={() => setDialog({ kind: 'stock' })}>
                Imprimir para estoque
              </button>
            }
          >
            As impressoras estão livres. Mande imprimir um produto para o estoque ou registre um
            pedido.
          </EmptyState>
        </section>
      ) : null}
      {!empty && view === 'board' ? (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 22,
            alignItems: 'flex-start',
            marginTop: 18,
          }}
        >
          <div className="col" style={{ flex: '3 1 520px', minWidth: 0, gap: 18 }}>
            {lanes.map((lane) => {
              const own = jobs.filter((j) => (j.printer?.id ?? null) === lane.id);
              const printing = own.find((j) => j.status === 'printing');
              const queued = own.filter((j) => j.status === 'queued');
              const first = queued[0];
              return (
                <section key={lane.id ?? 'none'} className="col" style={{ gap: 10 }}>
                  <div className="row between">
                    <h2 style={{ fontSize: 18 }}>
                      {lane.name}{' '}
                      <span className={printing ? 'badge info' : 'badge ok'}>
                        {printing ? 'Imprimindo' : 'Livre'}
                      </span>
                    </h2>
                    <span className="xs muted">↑ ↓ ou arrastar para reordenar</span>
                  </div>
                  {printing ? (
                    <PrintingCard
                      now={now}
                      job={printing}
                      onFinish={() => openDialog('finish', printing)}
                      onFail={() => openDialog('fail', printing)}
                    />
                  ) : lane.id ? (
                    <div className="card row between" style={{ gap: 10 }}>
                      <span className="sm">
                        {first
                          ? first.problems.some((p) => p.blocking)
                            ? `Resolva os avisos de ${first.number} para iniciar.`
                            : `${lane.name} livre. Pronta para começar ${first.number}.`
                          : 'Nada na fila.'}
                      </span>
                    </div>
                  ) : null}
                  {queued.map((j, i) => {
                    const b = badgeFor(j);
                    return (
                      <div
                        key={j.id}
                        className="card col"
                        style={{
                          gap: 8,
                          cursor: 'grab',
                          outline: dragId === j.id ? '2px dashed var(--pe)' : undefined,
                        }}
                        draggable
                        onDragStart={() => setDragId(j.id)}
                        onDragOver={(e) => {
                          if (dragId) e.preventDefault();
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          const dragged = jobs.find((x) => x.id === dragId);
                          if (dragged && dragged.id !== j.id) {
                            move.mutate(
                              (dragged.printer?.id ?? null) === lane.id
                                ? { job: dragged, body: { position: i } }
                                : { job: dragged, body: { printerId: lane.id } },
                            );
                          }
                          setDragId(null);
                        }}
                      >
                        <div className="row" style={{ gap: 12 }}>
                          <span className="fr tab muted" style={{ width: 20 }}>
                            {j.position}
                          </span>
                          <Thumb job={j} />
                          <button
                            type="button"
                            className="grow col"
                            style={{
                              gap: 2,
                              minWidth: 0,
                              textAlign: 'left',
                              background: 'none',
                              border: 0,
                              padding: 0,
                              cursor: 'pointer',
                              font: 'inherit',
                              color: 'inherit',
                            }}
                            onClick={() => setOpenId(j.id)}
                          >
                            <b className="trunc">{j.label}</b>
                            <span className="xs muted">{jobMeta(j)}</span>
                          </button>
                          <span className={b.cls}>{b.text}</span>
                          {i === 0 && !printing && lane.id ? (
                            <button
                              type="button"
                              className="btn sm p"
                              disabled={start.isPending}
                              onClick={() => {
                                if (j.problems.some((p) => p.blocking)) {
                                  setOpenId(j.id);
                                  toast(`Resolva os avisos de ${j.number} antes de iniciar.`);
                                } else start.mutate(j);
                              }}
                            >
                              Iniciar
                            </button>
                          ) : null}
                          <span className="col" style={{ gap: 2 }}>
                            <button
                              type="button"
                              className="btn sm icon"
                              aria-label={`Subir ${j.number}`}
                              disabled={i === 0}
                              onClick={() => move.mutate({ job: j, body: { direction: 'up' } })}
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              className="btn sm icon"
                              aria-label={`Descer ${j.number}`}
                              disabled={i === queued.length - 1}
                              onClick={() => move.mutate({ job: j, body: { direction: 'down' } })}
                            >
                              ↓
                            </button>
                          </span>
                        </div>
                        <Problems job={j} compact />
                      </div>
                    );
                  })}
                  {queued.length ? (
                    <div className="row between">
                      <span className="xs muted">Nada na fila depois desta.</span>
                      <button
                        type="button"
                        className="btn sm g"
                        onClick={() => setDialog({ kind: 'stock' })}
                      >
                        Imprimir para estoque
                      </button>
                    </div>
                  ) : null}
                </section>
              );
            })}
          </div>
          <aside className="col" style={{ flex: '1 1 280px', gap: 18 }}>
            {compatJob ? (
              <section className="card col" style={{ gap: 8 }}>
                <h2 style={{ fontSize: 18 }}>
                  Verificação de compatibilidade · {compatJob.number}
                </h2>
                <CompatRows job={compatJob} />
                <SwapButtons job={compatJob} />
              </section>
            ) : null}
            <TodayCard onOpen={(j) => setOpenId(j.id)} />
            <section className="card col" style={{ gap: 6 }}>
              <h2 style={{ fontSize: 18 }}>Carretéis</h2>
              <p className="sm muted">
                Troque ou carregue carretéis pela impressora ou pelo QR da etiqueta.
              </p>
              <Link
                className="btn sm"
                to="/filamentos/carreteis"
                style={{ alignSelf: 'flex-start' }}
              >
                Ver carretéis
              </Link>
            </section>
          </aside>
        </div>
      ) : null}
      {!empty && view === 'list' ? (
        <section className="card flat" style={{ marginTop: 18 }}>
          <div className="gt" style={{ overflowX: 'auto' }}>
            <div className="gtr head" style={{ gridTemplateColumns: listCols, minWidth: 720 }}>
              <span>Impressão</span>
              <span>Peça</span>
              <span>Destino</span>
              <span className="num">Tempo</span>
              <span className="num">Filamento</span>
              <span>Situação</span>
            </div>
            {all.map((j) => (
              <button
                key={j.id}
                type="button"
                className="gtr"
                style={{ gridTemplateColumns: listCols, minWidth: 720 }}
                onClick={() => setOpenId(j.id)}
              >
                <span className="tab">{j.number}</span>
                <span className="trunc">{j.label}</span>
                <span className="sm trunc">{destText(j)}</span>
                <span className="num">{formatHm(j.actualSeconds ?? j.estSeconds)}</span>
                <span className="num">{Math.round(j.actualGrams ?? j.estGrams)} g</span>
                <span>
                  {j.status === 'printing' ? (
                    <span className="badge info">Imprimindo · {j.progressPct ?? 0}%</span>
                  ) : j.status === 'queued' ? (
                    <span className={badgeFor(j).cls}>
                      {j.problems.some((p) => p.blocking)
                        ? badgeFor(j).text
                        : `Na fila · ${j.position}º`}
                    </span>
                  ) : j.status === 'done' ? (
                    <span className="badge ok">Concluída</span>
                  ) : (
                    <span className="badge err">Falhou {j.failPct ?? 0}%</span>
                  )}
                </span>
              </button>
            ))}
          </div>
        </section>
      ) : null}
      {dialogs}
    </>
  );
}
